import React, { useCallback, useMemo } from 'react';
import {
  Navigate,
  Outlet,
  createBrowserRouter as createBrowserRouterOriginal,
  RouterProvider,
} from 'react-router-dom';

import { BreadcrumbProvider } from './Breadcrumbs';
import { KnowledgeBaseProvider, ActiveKnowledgeBase } from './KnowledgeBase';

import {
  ScreensProvider,
  useLinkBasename,
  useAvailablePageIds,
  useNoAccess,
} from './ScreensContext';
import { SearchParamsStorage } from './Storage';

const normalizeSlashes = (path) => path.replace(/\/\/+/g, '/');
const joinPaths = (paths) => normalizeSlashes(paths.join('/'));

export const PUBLIC_SCREEN_PERMISSION = Math.random();

function getLinksMap(screens, parentPath) {
  return screens.reduce((linksMap, { link, children = [] }) => {
    const path = joinPaths(parentPath ? [parentPath, link.path] : [link.path]);
    return new Map([
      ...linksMap,
      ...getLinksMap(children, path),
      [link.key, parentPath],
    ]);
  }, new Map());
}

export const createScreenRoute = ({
  link,
  title,
  icon,
  children,
  id,
  screen,
  breadcrumbLink = true,
  navigationLink = true,
  navigationHidden = true,
  permission = PUBLIC_SCREEN_PERMISSION,
  hidden = false,
  storage,
  loader,
  mergeScreenStateWithLoadData,
  disabledSchemaValidation,
  knowledgeBaseUrl,
}) => {
  return {
    link: link,
    icon: icon,
    title: title,
    children: children,
    breadcrumbLink: breadcrumbLink,
    navigationLink: navigationLink,
    navigationHidden: navigationHidden,
    id: id,
    permission: permission,
    permissions:
      permission !== PUBLIC_SCREEN_PERMISSION
        ? [permission]
        : children
          ? [].concat(...children.map(({ permissions }) => permissions))
          : [PUBLIC_SCREEN_PERMISSION],
    screen: screen,
    hidden: hidden,
    storage: storage,
    loader,
    mergeScreenStateWithLoadData,
    disabledSchemaValidation,
    knowledgeBaseUrl,
  };
};

const createRoutesFromScreens = ({
  screens,
  ScreenWrapper,
  onMatch,
  errorBoundary: ErrorBoundary,
}) => {
  return screens.map(
    ({
      link,
      screen,
      permissions,
      breadcrumbLink,
      children,
      icon,
      title,
      storage: Storage = SearchParamsStorage,
      loader,
      mergeScreenStateWithLoadData,
      disabledSchemaValidation,
      knowledgeBaseUrl,
    }) => {
      const route = {
        path: `${link.path}/*`,
        caseSensitive: true,
        element: (
          <PageGuard permissions={permissions}>
            <BreadcrumbProvider
              key={title.toLocaleString()}
              title={title}
              icon={icon}
              link={link}
              linkable={breadcrumbLink}
            >
              {screen ? (
                <Storage
                  searchParams={link.searchParams}
                  link={link}
                  mergeScreenStateWithLoadData={mergeScreenStateWithLoadData}
                  disabledSchemaValidation={disabledSchemaValidation}
                >
                  <ScreenWrapper title={title.toLocaleString()}>
                    <ActiveKnowledgeBase url={knowledgeBaseUrl}>
                      {screen}
                    </ActiveKnowledgeBase>
                  </ScreenWrapper>
                </Storage>
              ) : (
                <Outlet />
              )}
            </BreadcrumbProvider>
          </PageGuard>
        ),
        errorElement: <ErrorBoundary />,
        loader,
      };

      if (children) {
        route.children = [
          ...createRoutesFromScreens({
            screens: children,
            ScreenWrapper,
            onMatch,
            errorBoundary: ErrorBoundary,
          }),
          {
            path: '*',
            element: <Navigate to={'/'} />,
          },
        ];
      }

      return route;
    }
  );
};

function createNavigationFromScreens(screens, isAllowed = () => true) {
  return screens
    .map((screen) => {
      const { link, title, id, hidden, children, navigationLink } = screen;
      if (hidden || !isAllowed(screen)) {
        return null;
      }
      let searchKey = title.toLowerCase();
      let nav = {
        link: link,
        title: title,
        id: id,
        linkable: navigationLink,
        searchKey: searchKey,
      };

      if (children) {
        nav.children = createNavigationFromScreens(children, isAllowed);
        searchKey = [
          searchKey,
          ...nav.children.map(({ searchKey }) => searchKey),
        ].join('');
      }

      nav.searchKey = searchKey;

      return nav;
    })
    .filter((navItem) => navItem);
}

const createPages = (screens, path = []) => {
  return screens.reduce(
    (pageList, { title, permission, navigationLink, children, hidden }) => {
      if (navigationLink && permission !== PUBLIC_SCREEN_PERMISSION) {
        pageList.push({
          id: permission,
          path: path,
          title: title,
          hidden: hidden,
        });
      }

      if (children && children.length) {
        pageList = [...pageList, ...createPages(children, [...path, title])];
      }

      return pageList;
    },
    []
  );
};

const NotFound = ({ link }) => {
  const basename = useLinkBasename(link);
  const to = link.location({}, { basename: basename });
  return <Navigate to={to} replace />;
};

const defaultScreenWrapper = ({ children }) => children;
const EMPTY_ROUTS = [];

const PageGuard = ({ children, permissions }) => {
  const ids = useAvailablePageIds();
  const NoAccess = useNoAccess();

  if (permissions.some((p) => ids?.includes(p))) {
    return children;
  }
  return <NoAccess />;
};

export const Screens = ({
  screens,
  actionRouts = EMPTY_ROUTS,
  ids,
  children,
  defaultScreenLink,
  ScreenWrapper = defaultScreenWrapper,
  onMatch,
  basename,
  fallback,
  NoAccess,
  defaultKnowledgeBaseUrl,
  createBrowserRouter = createBrowserRouterOriginal,
  errorBoundary,
}) => {
  const isAllowed = React.useCallback(
    ({ permissions }) => permissions.some((p) => ids.includes(p)),
    [ids]
  );
  const navigation = useMemo(
    () => createNavigationFromScreens(screens, isAllowed),
    [screens, isAllowed]
  );
  const _basename = basename.replaceAll('/', '');

  const routes = useMemo(() => {
    return [
      {
        element: children,
        children: [
          ...createRoutesFromScreens({
            screens,
            ScreenWrapper,
            onMatch,
            errorBoundary,
          }),
          ...actionRouts,
          {
            path: '*',
            element: <NotFound link={defaultScreenLink} />,
          },
        ],
      },
    ];
  }, [screens, defaultScreenLink, onMatch, actionRouts]);

  const _linksMap = useMemo(() => getLinksMap(screens), [screens]);
  const pages = createPages(screens);

  const getLinkBasename = useCallback(
    (link) => (link ? _linksMap.get(link.key) : ''),
    [_linksMap]
  );
  const router = useMemo(
    () => createBrowserRouter(routes, { basename }),
    [routes, basename]
  );

  return (
    <ScreensProvider
      navigation={navigation}
      pages={pages}
      NoAccess={NoAccess}
      getLinkBasename={getLinkBasename}
      ids={ids}
      _basename={_basename}
    >
      <KnowledgeBaseProvider defaultUrl={defaultKnowledgeBaseUrl}>
        <RouterProvider router={router} fallbackElement={fallback} />
      </KnowledgeBaseProvider>
    </ScreensProvider>
  );
};
